<script lang="ts">
  import { goto } from "$app/navigation";
  import { page } from "$app/state";
  import { TableAPI } from "$lib/api/table";
  import { SVG } from "$lib/components";
  import { Button, DropdownMenu } from "$lib/components/ui";
  import { dotsHorizontalIcon } from "$lib/images/icons";
  import { authStore } from "$lib/stores";
  import { tableStore } from "$lib/stores/table-store";
  import { groupTables, tableHistoryStore } from "$lib/stores/tableHistoryStore";
  import ShortcutKey from "$lib/table/components/ShortcutKey.svelte";
  import type { TableInfo } from "$lib/types";
  import { cn } from "$lib/utility";
  import { Portal } from "bits-ui";
  import { Plus, Search, Table2, Trash2, Users } from "lucide-svelte";
  import { onMount } from "svelte";

  const { setShowCreateTableDialog } = tableStore;
  let isLoading = $state(true);
  let searchQuery = $state("");
  const groupedTableHistory = $derived(() => {
    const userId = authStore.getUser()?.id;

    // Separate tables into owned and shared
    const ownedTables = $tableHistoryStore.filter((table) => table.ownerId === userId);
    const sharedTables = $tableHistoryStore.filter((table) => table.ownerId !== userId);

    // Filter based on search query
    const currentTableId = page.params.table_id;
    const filterBySearch = (tables: TableInfo[]) =>
      searchQuery
        ? tables.filter(
            (table: TableInfo) =>
              table.name.toLowerCase().includes(searchQuery.toLowerCase()) || table.id === currentTableId,
          )
        : tables;

    const filteredOwnedTables = filterBySearch(ownedTables);
    const filteredSharedTables = filterBySearch(sharedTables);

    // Create the grouped object with owned and shared tables
    return {
      // Group owned tables by date
      ...groupTables(filteredOwnedTables),
      // Add shared tables as a separate section if there are any
      ...(filteredSharedTables.length > 0 ? { "Shared with me": filteredSharedTables } : {}),
    };
  });

  async function loadTables(): Promise<void> {
    const userId = authStore.getUser()?.id;
    if (!userId) return;

    isLoading = true;
    try {
      const tables = await TableAPI.getAllTables();

      if (tables && Array.isArray(tables)) {
        const processedTables = tables.map((table: TableInfo) => ({
          ...table,
          id: table.publicId,
          publicId: table.publicId,
          createdAt: new Date(table.createdAt),
          updatedAt: new Date(table.updatedAt),
          visibility: table.visibility as "private" | "public" | "organization",
        }));
        tableHistoryStore.setTables(processedTables);
      }
    } catch (err) {
      console.error("Error loading tables:", err);
    } finally {
      isLoading = false;
    }
  }

  async function handleDelete(id: string, _: string) {
    try {
      tableHistoryStore.removeTable(id);
      if (page.params.table_id === id) {
        goto("/table");
      }
      await TableAPI.deleteTable(id);
    } catch (err) {
      console.error("Error deleting table:", err);
      loadTables();
    }
  }

  onMount(() => {
    if (authStore.getUser()) {
      loadTables();
    } else {
      isLoading = false;
    }
  });
</script>

<div class="relative">
  {#if $tableStore.isInShortcutMode && !$tableStore.currentShortcutSequence}
    <ShortcutKey key="N" className="absolute top-1/2 -translate-x-full -translate-y-1/2 size-5" />
  {/if}
  <button
    id="new-table-button"
    onclick={() => setShowCreateTableDialog(true)}
    class={cn(
      "mb-3 flex h-8 w-full items-center justify-center !rounded-base border-1 border-theme-text-primary p-0 text-xs focus:outline-none focus:ring-0 ",
      " border-transparent bg-theme-green-medium font-medium text-white dark:bg-theme-green-bright dark:text-black",
    )}
  >
    <Plus size={24} strokeWidth={1} class="mr-2 hidden sm:inline-block" />
    New Table
  </button>
</div>
<div class={cn("relative mb-2")}>
  <Search strokeWidth={1} class="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-theme-text-secondary" />
  <input
    id="table-history-search"
    class={cn(
      "dark:focus:ring-theme-greenring-1 h-8 w-full !rounded-base border-sidebar-border pl-8 text-xs text-black ring-theme-text-primary ring-offset-0 focus:border-theme-text-primary focus:ring-0 focus:ring-theme-text-primary focus:ring-offset-0 dark:bg-theme-background-tertiary dark:text-theme-text-secondary dark:ring-theme-green dark:focus:border-theme-green",
      $tableStore.isInShortcutMode && !$tableStore.currentShortcutSequence ? " border-yellow-400" : "",
    )}
    bind:value={searchQuery}
    placeholder="Search"
  />
  {#if $tableStore.isInShortcutMode && !$tableStore.currentShortcutSequence}
    <ShortcutKey key="Q" className="absolute right-2 top-1/2 -translate-y-1/2 size-5" />
  {/if}
</div>

<div class="custom-scrollbar flex-1 overflow-y-auto overflow-x-hidden">
  <div class="mt-1.5 flex flex-col gap-5 text-xs">
    {#each Object.entries(groupedTableHistory()) as [group, tables]}
      {#if tables.length > 0}
        <div>
          <div class="mb-2 flex items-center gap-2 font-bold dark:text-theme-gray-500">
            {#if group === "Shared with me"}
              <Users strokeWidth={1} class="h-4 w-4" />
            {/if}
            {group}
          </div>
          <ul>
            {#each tables as table (`${group}-${table.id}-${Math.random()}`)}
              <li class="mb-3 flex items-center justify-between gap-1 rounded-base font-light">
                <button
                  class={cn(
                    "flex-grow truncate text-left lg:max-w-[200px]",
                    "text-theme-text-primary",
                    "hover:font-bold dark:hover:opacity-80",
                    "mr-2 transition-all",
                    "chatsheet-table-history-item",
                    page.params.table_id === table.id ? "font-bold dark:text-white" : "transition-default",
                  )}
                  onclick={() => goto(`/table/${table.id}`)}
                >
                  {table.name}
                </button>

                <div class="group/table-item flex items-center">
                  <span class="mr-2 flex items-center justify-between gap-2">
                    <Table2 class="inline h-3.5 w-3.5 dark:text-theme-gray-500" strokeWidth={1} />
                  </span>

                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                      <button
                        class="opacity-0 transition-opacity duration-200 group-hover/table-item:opacity-100 dark:text-theme-gray-500 dark:hover:text-theme-gray-800"
                      >
                        <SVG className="text-theme-text-primary dark:text-theme-gray-500" file={dotsHorizontalIcon} />
                      </button>
                    </DropdownMenu.Trigger>
                    <Portal>
                      <DropdownMenu.Content
                        side="top"
                        class="!rounded-base !p-0 shadow-sm dark:border dark:border-white/10 dark:bg-theme-background-tertiary"
                      >
                        <DropdownMenu.Item
                          class={cn(
                            "hover:cursor-pointer",
                            "text-theme-red dark:text-theme-gray-600 dark:hover:text-theme-red",
                            "flex items-center gap-2 text-xs",
                            "dark:hover:bg-theme-background-primary",
                          )}
                          onclick={() => handleDelete(table.publicId, table.name)}
                        >
                          <Trash2 class="h-4 w-4" strokeWidth={1} />
                          <span>Delete</span>
                        </DropdownMenu.Item>
                      </DropdownMenu.Content>
                    </Portal>
                  </DropdownMenu.Root>
                </div>
              </li>
            {/each}
          </ul>
        </div>
      {/if}
    {/each}

    {#if Object.entries(groupedTableHistory()).length === 0 && !isLoading}
      <div class="text-theme-text-secondary dark:text-theme-gray-500">Your sheets will appear here</div>
    {/if}
  </div>

  {#if isLoading}
    <div class="flex flex-col gap-6 pb-1.5 text-xs">
      {#each Array(3) as _, __}
        <div>
          <ul>
            {#each Array(2) as _, __}
              <li class="mb-4 flex animate-pulse items-center justify-between">
                <div class="h-3.5 w-[75%] rounded-base bg-gray-100 dark:bg-theme-background-tertiary"></div>
                <div class="mr-6 h-3.5 w-6 rounded-base bg-gray-100 dark:bg-theme-background-tertiary"></div>
              </li>
            {/each}
          </ul>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: black transparent;
  }
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: black;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background-color: transparent;
  }
</style>
